"use client";

import { useEffect, useState, useRef } from 'react';
import type {
  Query,
  DocumentData,
  QuerySnapshot,
} from 'firebase/firestore';
import { onSnapshot } from 'firebase/firestore';
import { useFirestore } from '../provider';
import { errorEmitter } from '../error-emitter';
import { FirestorePermissionError } from '../errors';

export function useCollection<T>(query: Query<T, DocumentData> | null) {
  const [snapshot, setSnapshot] = useState<QuerySnapshot<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const firestore = useFirestore();

  useEffect(() => {
    if (!firestore) {
      setLoading(false);
      // We can't query if firestore is not available. This is not an error, but a pending state.
      return;
    }
    if (!query) {
      setLoading(false);
      setError(new Error("Query is null or undefined."));
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      query,
      (snap) => {
        setSnapshot(snap);
        setLoading(false);
      },
      (err) => {
        console.error("Firestore onSnapshot error:", err);
        const permissionError = new FirestorePermissionError({
          path: (query as any)._query.path.segments.join('/'),
          operation: 'list',
        });
        errorEmitter.emit('permission-error', permissionError);
        setError(permissionError);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [firestore, query]);

  const data = snapshot?.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) || [];

  return { data, loading, error, snapshot };
}
